import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTopEmployeesThunk,
} from '../features/reports/reportThunks';

// Keyframes for skeleton loading animation
const keyframes = {
  pulse: `
    @keyframes pulse {
      0% {
        opacity: 0.6;
      }
      50% {
        opacity: 1;
      }
      100% {
        opacity: 0.6;
      }
    }
  `
};

const styles = {
  container: {
    backgroundColor: '#f0f0f0',
    padding: '15px',
    borderRadius: '12px',
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    width:'46%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '40px'
  },
  dropdown: {
    position: 'relative',
    display: 'inline-block'
  },
  dropdownButton: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '15px',
    fontWeight: '600',
    color: '#333',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '8px 0'
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'white',
    color: '#333',
    fontWeight: '600',
    padding: '8px 16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  filterMenu: {
    position: 'absolute',
    right: '0',
    marginTop: '8px',
    width: '180px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    border: '1px solid #eee',
    zIndex: '10',
    display: 'block'
  },
  filterItem: {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    textAlign: 'left',
    fontSize: '14px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer'
  },
  filterItemActive: {
    backgroundColor: '#f0f0f0',
    fontWeight: '600'
  },
  title: {
    fontSize: '16px',
    fontWeight: '400',
    color: '#333',
    margin: '14px 0 0 0'
  },
  subtitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#555',
    margin: '4px 0  0',
    display: 'flex',
    alignItems: 'center'
  },
  chartContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: '200px'
  },
  barColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '18%'
  },
  bar: {
    width: '100%',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: '16px',
    marginBottom: '12px',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,1) 100%)',
    boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
  },
  profileImage: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid white',
    objectFit: 'cover' // Ensure image covers the circle properly
  },
  initialsCircle: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid white',
    color: 'white',
    fontWeight: '400',
    fontSize: '13px'
  },
  count: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#555'
  },
  chevronIcon: {
    width: '16px',
    height: '16px',
    marginLeft: '8px'
  },
  barIcon: {
    width: '24px',
    height: '24px',
    marginRight: '8px'
  },
  filterIcon: {
    width: '16px',
    height: '16px',
    marginLeft: '8px'
  },
  // Skeleton styles for loading state
  skeletonBar: {
    width: '100%',
    borderRadius: '8px',
    backgroundColor: '#e0e0e0',
    animation: 'pulse 1.5s infinite ease-in-out',
    marginBottom: '12px'
  },
  skeletonCircle: {
    width: '35px',
    height: '35px',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
    animation: 'pulse 1.5s infinite ease-in-out',
    marginTop: '16px'
  },
  skeletonCount: {
    width: '20px',
    height: '12px',
    borderRadius: '4px',
    backgroundColor: '#e0e0e0',
    animation: 'pulse 1.5s infinite ease-in-out'
  }
};

const COLOR_PALETTE = ['#4285F4', '#EA4335', '#34A853', '#FBBC05', '#9c27b0'];

// Skeleton component for loading state
const SkeletonBar = () => {
  return (
    <div style={styles.barColumn}>
      <div style={{...styles.skeletonBar, height: '120px'}}>
        <div style={styles.skeletonCircle}></div>
      </div>
      <div style={styles.skeletonCount}></div>
    </div>
  );
};

// Icon components
const ChevronIcon = () => (
  <svg style={styles.chevronIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const FilterIcon = () => (
  <svg style={styles.filterIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6H20M4 12H16M4 18H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const MenuIcon = () => (
  <svg style={styles.barIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TopPerformers = () => {
  const dispatch = useDispatch();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const { list: performers, loading } = useSelector(state => state.reports.topEmployees);

  // whenever filter changes, refetch
  useEffect(() => {
    const severityMap = { high: 'High', medium: 'Medium', low: 'Low' };
    const severity = severityMap[selectedFilter] || undefined;
    dispatch(fetchTopEmployeesThunk({ severity }));
  }, [dispatch, selectedFilter]);

  // helper to get count property
  const getCount = p => p.report_count;

  // sort descending
  const sorted = [...(performers || [])].sort((a, b) => getCount(b) - getCount(a));
  const maxValue = sorted.reduce((max, p) => Math.max(max, getCount(p)), 0);

  const initials = name =>
    name.split(' ').map(n => n[0]).join('');

  // Helper to get employee image from various possible field names
  const getEmployeeImage = (employee) => {
    // Check for common image field names
    const possibleImageFields = [
      'image_url', 'avatar', 'avatar_url', 
      'photo', 'photo_url', 'profile_image', 
      'profile_picture', 'image'
    ];
    
    for (const field of possibleImageFields) {
      if (employee[field]) {
        return employee[field];
      }
    }
    
    // No image found
    return null;
  };

  const toggleFilters = () => setShowFilters(v => !v);
  const selectFilter = f => {
    setSelectedFilter(f);
    setShowFilters(false);
  };

  // Handle image error
  const handleImageError = (employeeId) => {
    setImageErrors(prev => ({
      ...prev,
      [employeeId]: true
    }));
  };

  return (
    <div style={styles.container}>
      {/* Add keyframes for skeleton animation */}
      <style>{keyframes.pulse}</style>
      
      <div style={styles.header}>
        <div style={styles.dropdown}>
          {/* You can hook this up to navigate or something */}
          <button style={styles.dropdownButton}>
            <MenuIcon />
          </button>
        </div>

        <div style={styles.dropdown}>
          <button style={styles.filterButton} onClick={toggleFilters}>
            Filters
            <FilterIcon />
          </button>
          {showFilters && (
            <div style={styles.filterMenu}>
              {['all','high','medium','low'].map(f => (
                <button
                  key={f}
                  style={{
                    ...styles.filterItem,
                    ...(selectedFilter === f ? styles.filterItemActive : {})
                  }}
                  onClick={() => selectFilter(f)}
                >
                  {f === 'all' ? 'All Severities' : `${f.charAt(0).toUpperCase() + f.slice(1)} Severity`}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={styles.chartContainer}>
        {loading ? (
          // Skeleton loading UI - show 5 skeleton bars
          Array(5).fill(0).map((_, idx) => (
            <SkeletonBar key={idx} />
          ))
        ) : (
          sorted.map((p, idx) => {
            const count = getCount(p);
            const height = 60 + (maxValue ? (count / maxValue) * 120 : 0);
            const imageUrl = getEmployeeImage(p);
            const hasImageError = imageErrors[p.employee_id];
            
            return (
              <div key={p.employee_id} style={styles.barColumn}>
                <div 
                  style={{ 
                    ...styles.bar, 
                    height: `${height}px`, 
                    backgroundColor: COLOR_PALETTE[idx % COLOR_PALETTE.length] 
                  }}
                >
                  {/* Show image if available and not errored, otherwise show initials */}
                  {imageUrl && !hasImageError ? (
                    <img 
                      src={imageUrl} 
                      alt={p.name}
                      style={styles.profileImage}
                      onError={() => handleImageError(p.employee_id)}
                    />
                  ) : (
                    <div 
                      style={{
                        ...styles.initialsCircle, 
                        backgroundColor: COLOR_PALETTE[idx % COLOR_PALETTE.length]
                      }}
                    >
                      {initials(p.name)}
                    </div>
                  )}
                </div>
                <span style={styles.count}>{count}</span>
              </div>
            );
          })
        )}
      </div>

      <h2 style={styles.title}>Reports amount</h2>
      <h3 style={styles.subtitle}>
        by top performers
        <ChevronIcon />
      </h3>
    </div>
  );
};

export default TopPerformers;